import { zodResolver } from '@hookform/resolvers/zod';
import { useForm } from 'react-hook-form';
import { z } from 'zod';
import { FormInputText } from '@/shared/components/composites/FormInputText';
import { FormSubmitButtons } from '@/shared/components/composites/FormSubmitButtons';
import { BasicContainer } from '@/shared/components/composites/containers/BasicContainer';
import { AlertMessageBox } from '@/shared/components/parts/AlertMessageBox';
import { Spacer } from '@/shared/components/parts/Spacer';
import { zodValidations } from '@/shared/helpers/zod/zodValidations';
import { useEmailInput } from '@/shared/hooks/useEmailInput';
import { usePasswordInput } from '@/shared/hooks/usePasswordInput';
import type { UseUserCreateStart } from '../index.hook';

type FormValues = UseUserCreateStart['state']['formValues'];

export const InputLoginInfoForm: React.FC<{
  state: UseUserCreateStart['state'];
  loading: UseUserCreateStart['loading'];
  sendVerificationCode: UseUserCreateStart['sendVerificationCode'];
}> = ({ state, loading, sendVerificationCode }) => {
  const {
    register,
    handleSubmit,
    setValue,
    formState: { errors },
  } = useForm<FormValues>({
    mode: 'onTouched',
    defaultValues: state.formValues,
    resolver: zodResolver(
      z.object({
        mailAddress: zodValidations.email,
        password: zodValidations.password,
      })
    ),
  });

  const { emailInputProps } = useEmailInput({
    fieldName: 'mailAddress' as const,
    setFormValue: setValue,
  });

  const { passwordInputProps } = usePasswordInput({
    fieldName: 'password' as const,
    setFormValue: setValue,
  });

  return (
    <BasicContainer>
      <AlertMessageBox message={state.errorMessage} />
      <div className="text-base2 font-bold cst-halt leading-[1.4] text-main-100">
        STEP 1/2
      </div>
      <Spacer spacing={2} />
      <div>ログインID/パスワードを入力してください。</div>
      <Spacer spacing={8} />
      <form onSubmit={handleSubmit(sendVerificationCode)}>
        <FormInputText
          {...register('mailAddress')}
          {...emailInputProps}
          type="email"
          validationMessage={errors.mailAddress?.message}
          autoComplete="username"
          label="メールアドレス（ログインID）"
        />
        <Spacer spacing={4} />
        <FormInputText
          {...register('password')}
          {...passwordInputProps}
          type="password"
          validationMessage={errors.password?.message}
          autoComplete="new-password"
          label="パスワード"
        />
        <Spacer spacing={6} />
        <div className="text-center text-base2">
          <span className="inline-block">
            登録ボタンをクリックすることにより
          </span>
          <span className="inline-block">
            <a
              href="https://www.unext.co.jp/ja/legal/privacy"
              target="_blank"
              rel="noopener noreferrer"
              className="text-accent-primary underline"
            >
              個人情報の取扱いについて
            </a>
          </span>
          &nbsp;
          <span className="inline-block">
            <a
              href="https://help.unext.jp/legal/terms"
              target="_blank"
              rel="noopener noreferrer"
              className="text-accent-primary underline"
            >
              利用規約
            </a>
          </span>
          <span className="inline-block">に同意するものとします。</span>
        </div>
        <Spacer spacing={12} />
        <FormSubmitButtons
          buttons={[
            {
              uiType: 'buttonPrimary',
              text: '登録',
              type: 'submit',
              isLoading: loading,
              disabled: loading,
              id: 'linkId-registration-account-cta1',
            },
          ]}
        />
      </form>
    </BasicContainer>
  );
};
